import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Typography } from "@mui/material";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutlineOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import LinkIcon from "@mui/icons-material/Link";

import PageContainer from "../layouts/PageContainer";
import PageToolbar from "../components/common/PageToolbar";
import BranchSelectFilter from "../components/common/BranchSelectFilter";
import AppButton from "../components/buttons/AppButton";
import StandardTable, { TableColumn } from "../components/common/StandardTable";
import ConfirmDialog from "../components/common/ConfirmDialog";
import RowActionMenu, {
  RowActionDef,
} from "../components/common/RowActionMenu";

import CreateParentModal from "../components/parents/CreateParentModal";
import UpdateParentModal from "../components/parents/UpdateParentModal";
import ViewParentModal from "../components/parents/ViewParentModal";
import LinkStudentModal from "../components/parents/LinkStudentModal";

import useDebounce from "../hooks/useDebounce";
import { usePermissions } from "../hooks/usePermissions";
import { useAppDispatch, useAppSelector } from "../redux/store";
import {
  getParents,
  getParentById,
  deleteParent,
  resetParentServerStatus,
} from "../redux/actions/parentActions";
import { ParentGuardianSummaryDto } from "../shared/dtos/parentGuardian/ParentGuardianSummaryDto";
import { ParentGuardianResponseDto } from "../shared/dtos/parentGuardian/ParentGuardianResponseDto";

const ParentsPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const { hasPermission } = usePermissions();

  const {
    items: parents,
    item: parent,
    loadingFetchAll: loadingParents,
    loadingFetchOne: loadingParent,
    errorFetchAll: errorLoadingParents,
    errorFetchOne: errorLoadingParent,
    pagination,
    serverStatus,
    loadingCreate: loadingParentCreate,
    errorCreate: errorCreatingParent,
    loadingUpdate: loadingParentUpdate,
    errorUpdate: errorUpdatingParent,
    loadingDelete: loadingParentDelete,
  } = useAppSelector((state) => state.parent);

  const [searchTerm, setSearchTerm] = useState<string>("");
  const debouncedSearch = useDebounce(searchTerm, 500);
  const [selectedBranchId, setSelectedBranchId] = useState<string>("");
  const [pageConfig, setPageConfig] = useState({ page: 1, limit: 10 });

  // Modal State
  const [isCreateModalOpen, setIsCreateModalOpen] = useState<boolean>(false);
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState<boolean>(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState<boolean>(false);
  const [isLinkModalOpen, setIsLinkModalOpen] = useState<boolean>(false);
  const [selectedParent, setSelectedParent] =
    useState<ParentGuardianSummaryDto | null>(null);

  const canCreate = hasPermission("parent:create");
  const canUpdate = hasPermission("parent:update");
  const canDelete = hasPermission("parent:delete");
  const canRead = hasPermission("parent:read");

  const fetchParentsList = useCallback((): void => {
    dispatch(
      getParents({
        page: pageConfig.page,
        size: pageConfig.limit,
        search: debouncedSearch.trim() || undefined,
        branchId: selectedBranchId || undefined,
      }),
    );
  }, [
    dispatch,
    pageConfig.page,
    pageConfig.limit,
    debouncedSearch,
    selectedBranchId,
  ]);

  useEffect(() => {
    fetchParentsList();
  }, [fetchParentsList]);

  // Modal Handlers
  const handleOpenCreate = (): void => setIsCreateModalOpen(true);
  const handleCloseCreate = useCallback((): void => {
    if (!loadingParentCreate) setIsCreateModalOpen(false);
  }, [loadingParentCreate]);

  const handleOpenUpdate = (parentRow: ParentGuardianSummaryDto): void => {
    dispatch(getParentById(parentRow.id));
    setSelectedParent(parentRow);
    setIsUpdateModalOpen(true);
  };
  const handleCloseUpdate = useCallback((): void => {
    if (!loadingParentUpdate) {
      setIsUpdateModalOpen(false);
      setSelectedParent(null);
    }
  }, [loadingParentUpdate]);

  const handleOpenLink = (parentRow: ParentGuardianSummaryDto): void => {
    setSelectedParent(parentRow);
    setIsLinkModalOpen(true);
  };
  const handleCloseLink = useCallback((): void => {
    setIsLinkModalOpen(false);
    setSelectedParent(null);
  }, []);

  const handleOpenDelete = (parentRow: ParentGuardianSummaryDto): void => {
    setSelectedParent(parentRow);
    setIsDeleteModalOpen(true);
  };
  const handleCloseDelete = useCallback((): void => {
    if (!loadingParentDelete) {
      setIsDeleteModalOpen(false);
      setSelectedParent(null);
    }
  }, [loadingParentDelete]);

  const handleOpenView = (parentRow: ParentGuardianSummaryDto): void => {
    dispatch(getParentById(parentRow.id));
    setIsViewModalOpen(true);
  };
  const handleCloseView = (): void => setIsViewModalOpen(false);

  const handleDeleteConfirm = (): void => {
    if (selectedParent) {
      dispatch(deleteParent(selectedParent.id));
    }
  };

  useEffect(() => {
    if (!serverStatus) return;
    const { operation, status } = serverStatus;

    if (operation === "create" && status === 201) handleCloseCreate();
    if (operation === "update" && status === 200) handleCloseUpdate();
    if (operation === "delete" && status === 200) handleCloseDelete();
    if (operation === "link" && status === 200) handleCloseLink();

    dispatch(resetParentServerStatus());
  }, [
    serverStatus,
    dispatch,
    handleCloseCreate,
    handleCloseUpdate,
    handleCloseDelete,
    handleCloseLink,
  ]);

  const columns = useMemo<TableColumn<ParentGuardianSummaryDto>[]>(
    () => [
      {
        id: "name",
        label: "Full Name",
        minWidth: 170,
        format: (_, row) => (
          <Typography
            component="span"
            sx={{ fontWeight: 600, fontSize: "0.875rem" }}
          >
            {row.firstName} {row.lastName}
          </Typography>
        ),
      },
      { id: "email", label: "Email Address", minWidth: 200 },
      { id: "primaryPhoneNumber", label: "Primary Phone", minWidth: 150 },
      {
        id: "occupation",
        label: "Occupation",
        minWidth: 150,
        format: (value) => String(value || "N/A"),
      },
    ],
    [],
  );

  const renderRowActions = (row: ParentGuardianSummaryDto): React.ReactNode => {
    const inlineActions: RowActionDef[] = [
      {
        label: "View",
        icon: <VisibilityOutlinedIcon fontSize="small" />,
        color: "info",
        onClick: () => handleOpenView(row),
        show: canRead,
      },
    ];

    const menuActions: RowActionDef[] = [
      {
        label: "Edit Parent",
        icon: <EditOutlinedIcon fontSize="small" />,
        onClick: () => handleOpenUpdate(row),
        show: canUpdate,
      },
      {
        label: "Link to Student",
        icon: <LinkIcon fontSize="small" />,
        onClick: () => handleOpenLink(row),
        show: canUpdate,
      },
      {
        label: "Delete Parent",
        icon: <DeleteOutlineIcon fontSize="small" />,
        color: "error",
        onClick: () => handleOpenDelete(row),
        show: canDelete,
      },
    ];

    return (
      <RowActionMenu inlineActions={inlineActions} menuActions={menuActions} />
    );
  };

  return (
    <PageContainer
      title="Parent & Guardian Directory"
      subtitle="Manage parent details and student associations."
      actions={
        canCreate && (
          <AppButton
            variant="contained"
            startIcon={<PersonAddIcon />}
            onClick={handleOpenCreate}
          >
            Add Parent
          </AppButton>
        )
      }
    >
      <PageToolbar
        searchTerm={searchTerm}
        onSearchChange={(e) => {
          setSearchTerm(e.target.value);
          setPageConfig((prev) => ({ ...prev, page: 1 }));
        }}
        searchPlaceholder="Search by name, email, or phone..."
      >
        <BranchSelectFilter
          value={selectedBranchId}
          onChange={(bId) => {
            setSelectedBranchId(bId);
            setPageConfig((p) => ({ ...p, page: 1 }));
          }}
        />
      </PageToolbar>

      <StandardTable<ParentGuardianSummaryDto>
        columns={columns}
        data={parents}
        loading={loadingParents}
        error={errorLoadingParents}
        onRetry={fetchParentsList}
        page={pageConfig.page}
        count={pagination?.totalElements || 0}
        rowsPerPage={pageConfig.limit}
        onPageChange={(newPage) =>
          setPageConfig((prev) => ({ ...prev, page: newPage }))
        }
        onRowsPerPageChange={(newLimit) =>
          setPageConfig((prev) => ({ ...prev, limit: newLimit, page: 1 }))
        }
        rowAction={renderRowActions}
      />

      {isCreateModalOpen && (
        <CreateParentModal
          open={isCreateModalOpen}
          onClose={handleCloseCreate}
          loading={loadingParentCreate}
          error={errorCreatingParent}
        />
      )}

      {isUpdateModalOpen && selectedParent && (
        <UpdateParentModal
          open={isUpdateModalOpen}
          onClose={handleCloseUpdate}
          parent={
            (parent?.id === selectedParent.id
              ? parent
              : selectedParent) as ParentGuardianResponseDto
          }
          loading={loadingParentUpdate || loadingParent}
          error={errorUpdatingParent || errorLoadingParent}
        />
      )}

      {isLinkModalOpen && selectedParent && (
        <LinkStudentModal
          open={isLinkModalOpen}
          onClose={handleCloseLink}
          parent={selectedParent}
          loading={loadingParentUpdate}
          error={null}
        />
      )}

      {isViewModalOpen && (
        <ViewParentModal
          open={isViewModalOpen}
          onClose={handleCloseView}
          parent={parent}
          loading={loadingParent}
          error={errorLoadingParent}
        />
      )}

      {isDeleteModalOpen && selectedParent && (
        <ConfirmDialog
          open={isDeleteModalOpen}
          onClose={handleCloseDelete}
          onConfirm={handleDeleteConfirm}
          title="Delete Parent Profile"
          description={`Are you sure you want to delete ${selectedParent.firstName} ${selectedParent.lastName}? This will also remove any user login associated with this profile.`}
          loading={loadingParentDelete}
        />
      )}
    </PageContainer>
  );
};

export default ParentsPage;
